import {
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  Component,
  SimpleChanges
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { of, tap, finalize, catchError, Subscription } from 'rxjs';

import { AuthUser } from '@shared/signup/models';
import { WhoService } from '../../../../../services';
import { AppIcon, AppIconSize } from '@tablesready/frontend-icons';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';

@Component({
  selector: 'tablesready-reserve-with-google',
  templateUrl: './reserve-with-google.component.html',
})
export class ReserveWithGoogleComponent implements OnInit, OnChanges, OnDestroy {
  @Input() locationOnBusinessPlan = false;

  disabled: boolean = true;
  form: FormGroup;
  user: AuthUser;
  icon = AppIcon;
  size = AppIconSize;
  sending = false;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly whoService: WhoService,
    private readonly alertService: AlertsService,
    private readonly authRestaurantService: AuthRestaurantService,
  ) {
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({ enabled: [false, [Validators.required]] });
    this.authRestaurantService.selectWithAccount()
      .pipe(tap((user) => {
        this.user = user;
        this.disabled = this.disableCheckbox(this.user);
        this.enabledControl.patchValue(this.user.location.integrations?.reserveWithGoogle?.enabled || false);
        this.toggleFormEnabled();
        if (
          this.user.location.integrations?.reserveWithGoogle?.enabled ||
          (
            this.user.location.publicWaitlist.add &&
            this.user.location.websiteUrl &&
            this.user.location.messages.smsConfirmation.enabled &&
            this.user.location.messages.smsWaitlistCancellation.enabled &&
            !this.disabled
          )
        ) {
          this.enabledControl.patchValue(this.enabledControl.value, { disabled: false });
        } else {
          this.enabledControl.patchValue(this.enabledControl.value, { disabled: true });
        }
      }))
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.locationOnBusinessPlan.previousValue !== undefined) {
      this.disabled = this.disableCheckbox(this.user);
      this.enabledControl.patchValue(this.enabledControl.value, { disabled: this.disabled });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get enabledControl(): AbstractControl {
    return this.form.get('enabled');
  }

  save() {
    this.whoService.reserveWithGoogle(this.form.value)
      .pipe(
        catchError(error => {
          this.alertService.add({
            type: IAlertTypes.danger,
            heading: 'Error saving integration with google.',
            body: error,
          });

          return of();
        }),
        tap(() => this.form.markAsPristine()),
        finalize(() => this.sending = false),
      )
      .subscribe();
  }

  private disableCheckbox(user: AuthUser): boolean {
    if (!user.location || user.location.integrations?.reserveWithGoogle?.enabled) {
      // Always allow user to disable the integration if it's enabled
      return false;
    }

    return !this.locationOnBusinessPlan;
  }

  private toggleFormEnabled(): void {
    if (!this.form) {
      return;
    }

    if (this.disabled) {
      this.form.disable({
        onlySelf: true,
        emitEvent: false,
      });
    } else {
      this.form.enable({
        onlySelf: true,
        emitEvent: false,
      });
    }
  }
}
